import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Package, Plus, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const emptyForm = {
  name: '', total_amount: '', activation_amount: '', balance_amount: '',
  anchor_amount: 12000,
  inclusions: '', timeline_days: 7, revisions_max: 2,
  is_default: false, is_active: true,
};

export const SmbPackagesCard = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get('/smb/packages'); if (r.data?.success) setRows(r.data.data || []); }
    catch {/* perm */}
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        total_amount: parseFloat(form.total_amount) || 0,
        activation_amount: parseFloat(form.activation_amount) || 0,
        balance_amount: parseFloat(form.balance_amount) || 0,
        anchor_amount: parseFloat(form.anchor_amount) || 12000,
        timeline_days: parseInt(form.timeline_days) || 7,
        revisions_max: parseInt(form.revisions_max) || 2,
        inclusions: form.inclusions.split('\n').map(s => s.trim()).filter(Boolean),
      };
      if (editingId) await api.put(`/smb/packages/${editingId}`, payload);
      else await api.post('/smb/packages', payload);
      toast.success(editingId ? 'Package updated' : 'Package created');
      setForm(emptyForm); setEditingId(null); load();
    } catch (er) { toast.error(er.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const edit = (pkg) => {
    setEditingId(pkg.id);
    setForm({
      name: pkg.name || '', total_amount: pkg.total_amount, activation_amount: pkg.activation_amount,
      balance_amount: pkg.balance_amount,
      anchor_amount: pkg.anchor_amount ?? 12000,
      inclusions: (pkg.inclusions || []).join('\n'),
      timeline_days: pkg.timeline_days || 7, revisions_max: pkg.revisions_max || 2,
      is_default: !!pkg.is_default, is_active: pkg.is_active !== false,
    });
  };

  const remove = async (id) => {
    if (!window.confirm('Deactivate this package?')) return;
    try { await api.delete(`/smb/packages/${id}`); toast.success('Deactivated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <Card data-testid="smb-packages-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Package className="h-5 w-5 text-purple-500" />SMB Activation Packages
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {rows.length > 0 && (
          <div className="space-y-2">
            {rows.map(p => (
              <div key={p.id} className={`flex items-center justify-between gap-3 p-3 rounded-md border ${p.is_active===false?'opacity-50':''}`} data-testid={`pkg-row-${p.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{p.name}</p>
                    {p.is_default && <Badge className="bg-amber-500/15 text-amber-500 text-[10px]"><Star className="h-2.5 w-2.5 mr-0.5" />default</Badge>}
                    {p.is_active === false && <Badge variant="outline" className="text-[10px]">inactive</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    ₹{p.total_amount} total · ₹{p.activation_amount} activation · {p.timeline_days}d · {p.revisions_max} revisions
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => edit(p)} data-testid={`pkg-edit-${p.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(p.id)} data-testid={`pkg-del-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={save} className="space-y-3 p-4 rounded-md bg-muted/30">
          <p className="text-xs font-semibold flex items-center gap-1"><Plus className="h-3 w-3" />{editingId ? 'Edit package' : 'New package'}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name *"><Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="pkg-name" /></Field>
            <Field label="Timeline (days)"><Input type="number" value={form.timeline_days} onChange={e => setForm({...form, timeline_days: e.target.value})} data-testid="pkg-timeline" /></Field>
            <Field label="Total (₹) *"><Input required type="number" value={form.total_amount} onChange={e => setForm({...form, total_amount: e.target.value})} data-testid="pkg-total" /></Field>
            <Field label="Activation (₹) *"><Input required type="number" value={form.activation_amount} onChange={e => setForm({...form, activation_amount: e.target.value})} data-testid="pkg-activation" /></Field>
            <Field label="Balance (₹) — auto"><Input type="number" value={form.balance_amount} onChange={e => setForm({...form, balance_amount: e.target.value})} data-testid="pkg-balance" /></Field>
            <Field label="Revisions"><Input type="number" value={form.revisions_max} onChange={e => setForm({...form, revisions_max: e.target.value})} data-testid="pkg-revisions" /></Field>
            {/* Anchor amount appears above the package price on the public
                activation page, framing the offer as a discount. */}
            <Field label="Anchor price (₹) — shown as 'market price'">
              <Input type="number" value={form.anchor_amount}
                     onChange={e => setForm({...form, anchor_amount: e.target.value})}
                     placeholder="12000" data-testid="pkg-anchor" />
            </Field>
          </div>
          <Field label="Inclusions (one per line)"><Textarea rows={4} value={form.inclusions} onChange={e => setForm({...form, inclusions: e.target.value})} data-testid="pkg-inclusions" /></Field>
          <div className="flex items-center gap-4">
            <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={form.is_default} onChange={e => setForm({...form, is_default: e.target.checked})} data-testid="pkg-default" />Default package</label>
            <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} data-testid="pkg-active" />Active</label>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy} data-testid="pkg-save">{busy ? 'Saving…' : (editingId ? 'Update' : 'Create')}</Button>
            {editingId && <Button type="button" variant="outline" onClick={() => { setEditingId(null); setForm(emptyForm); }}>Cancel</Button>}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>
);
